import { useNode, useEditor } from "@craftjs/core";
import React, { useState, useEffect, useCallback, useRef } from "react";
import { Box, Typography } from "@mui/material";
import { useResponsive } from "../../hooks/useResponsive";
import { sliderStyles, getSliderStyles } from "../../config/styleConfig";
import {
  selectedOutlineSx,
  labelStyle,
  inputStyle,
  itemCardStyle,
  addButtonStyle,
  removeButtonStyle,
  gridTwoColumnsStyle,
} from "../../config/settingsStyles";
import { SliderField, CheckboxField, ColorField, SettingsSection } from "../ui/form/SettingsField";
import { ImageUpload } from "../ui/ImageUpload";
import { ImageCropPicker } from "../ui/ImageCropPicker";

export interface HeroSlide {
  id: string;
  title: string;
  subtitle?: string;
  buttonText?: string;
  buttonLink?: string;
  backgroundImage?: string;
  backgroundPosition?: string;
  backgroundZoom?: number;
  backgroundColor?: string;
}

export interface HeroSliderProps {
  slides: HeroSlide[];
  minHeight?: number;
  autoPlay?: boolean;
  autoPlayInterval?: number;
  showDots?: boolean;
  showArrows?: boolean;
  titleColor?: string;
  subtitleColor?: string;
}

const defaultSlides: HeroSlide[] = [
  {
    id: "1",
    title: "Welcome to Our Clinic",
    subtitle: "Professional healthcare services",
    buttonText: "Book Now",
    buttonLink: "/contact",
    backgroundColor: "#1e3a5f",
  },
  {
    id: "2",
    title: "Special Promotion",
    subtitle: "Get 20% off this month",
    buttonText: "Learn More",
    buttonLink: "/services",
    backgroundColor: "#059669",
  },
];

export const HeroSlider = ({
  slides = defaultSlides,
  minHeight = 500,
  autoPlay = true,
  autoPlayInterval = 5000,
  showDots = true,
  showArrows = true,
  titleColor = "#ffffff",
  subtitleColor = "#e5e7eb",
}: HeroSliderProps) => {
  const {
    connectors: { connect, drag },
    selected,
  } = useNode((state) => ({
    selected: state.events.selected,
  }));

  // Check if we're inside Craft.js editor context (CMS mode)
  const { inEditor } = useEditor((state) => ({
    inEditor: true,
  }));

  const containerRef = useRef<HTMLDivElement>(null);
  const { isMobile } = useResponsive(containerRef as React.RefObject<HTMLElement>);

  // Get responsive styles from config
  const styles = getSliderStyles(isMobile);

  const [currentSlide, setCurrentSlide] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);

  const goToSlide = useCallback(
    (index: number) => {
      if (isTransitioning) return;
      setIsTransitioning(true);
      setCurrentSlide(index);
      setTimeout(() => setIsTransitioning(false), 500);
    },
    [isTransitioning]
  );

  const nextSlide = useCallback(() => {
    goToSlide((currentSlide + 1) % slides.length);
  }, [currentSlide, slides.length, goToSlide]);

  const prevSlide = useCallback(() => {
    goToSlide((currentSlide - 1 + slides.length) % slides.length);
  }, [currentSlide, slides.length, goToSlide]);

  // Auto-play
  useEffect(() => {
    if (!autoPlay || slides.length <= 1 || inEditor) return;
    const interval = setInterval(nextSlide, autoPlayInterval);
    return () => clearInterval(interval);
  }, [autoPlay, autoPlayInterval, nextSlide, slides.length, inEditor]);

  // Always prevent link navigation when in CMS editor
  const handleLinkClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (inEditor) {
      e.preventDefault();
      e.stopPropagation();
    }
  };

  return (
    <Box
      ref={(ref: HTMLDivElement | null) => {
        if (ref) {
          connect(drag(ref));
          (containerRef as React.MutableRefObject<HTMLDivElement | null>).current = ref;
        }
      }}
      sx={{
        position: "relative",
        overflow: "hidden",
        minHeight: isMobile ? `${Math.max(280, minHeight * 0.6)}px` : `${minHeight}px`,
        ...(selected && selectedOutlineSx),
      }}
    >
      {/* Slides */}
      {slides.map((slide, index) => (
        <Box
          key={slide.id}
          sx={{
            position: "absolute",
            inset: 0,
            transition: "all 0.5s ease-in-out",
            opacity: index === currentSlide ? 1 : 0,
            zIndex: index === currentSlide ? 10 : 0,
            backgroundColor: slide.backgroundColor || "#1e3a5f",
            overflow: "hidden",
          }}
        >
          {/* Background image with zoom/position */}
          {slide.backgroundImage && (
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                backgroundImage: `url(${slide.backgroundImage})`,
                backgroundSize: "cover",
                backgroundPosition: slide.backgroundPosition || "50% 50%",
                backgroundRepeat: "no-repeat",
                transform: `scale(${slide.backgroundZoom || 1})`,
                transformOrigin: slide.backgroundPosition || "50% 50%",
              }}
            />
          )}
          {/* Overlay - using config */}
          {slide.backgroundImage && (
            <Box sx={{ position: "absolute", inset: 0, bgcolor: sliderStyles.overlay.color }} />
          )}

          {/* Content */}
          <Box
            sx={{
              position: "relative",
              zIndex: 10,
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              textAlign: "center",
              px: 2,
            }}
          >
            <Box sx={{ maxWidth: "896px" }}>
              <Typography
                variant="h1"
                sx={{
                  fontFamily: "var(--font-family)",
                  fontSize: styles.title.fontSize,
                  fontWeight: 700,
                  mb: 2,
                  color: titleColor,
                }}
              >
                {slide.title}
              </Typography>
              {slide.subtitle && (
                <Typography
                  sx={{
                    fontFamily: "var(--font-family)",
                    fontSize: styles.subtitle.fontSize,
                    mb: 4,
                    color: subtitleColor,
                  }}
                >
                  {slide.subtitle}
                </Typography>
              )}
              {slide.buttonText && (
                <Box
                  component="a"
                  href={slide.buttonLink || "#"}
                  onClick={handleLinkClick}
                  sx={{
                    display: "inline-block",
                    px: `${styles.button.px}px`,
                    py: `${styles.button.py}px`,
                    borderRadius: 2,
                    fontWeight: 500,
                    fontSize: styles.button.fontSize,
                    textDecoration: "none",
                    transition: "all 0.2s",
                    background:
                      "linear-gradient(135deg, var(--color-primary, #3b82f6) 0%, var(--color-secondary, #10b981) 100%)",
                    color: "#ffffff",
                    "&:hover": {
                      transform: "scale(1.05)",
                      boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.1)",
                    },
                  }}
                >
                  {slide.buttonText}
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      ))}

      {/* Navigation Arrows - using config */}
      {showArrows && slides.length > 1 && (
        <>
          <Box
            component="button"
            onClick={prevSlide}
            sx={{
              position: "absolute",
              left: styles.arrow.position,
              top: "50%",
              transform: "translateY(-50%)",
              zIndex: 20,
              width: styles.arrow.size,
              height: styles.arrow.size,
              bgcolor: sliderStyles.arrowBackground.default,
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              border: "none",
              cursor: "pointer",
              backdropFilter: "blur(4px)",
              transition: "all 0.2s",
              "&:hover": { bgcolor: sliderStyles.arrowBackground.hover },
            }}
            aria-label="Previous slide"
          >
            <Box
              component="svg"
              sx={{
                width: styles.arrow.iconSize,
                height: styles.arrow.iconSize,
                color: "#ffffff",
              }}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </Box>
          </Box>
          <Box
            component="button"
            onClick={nextSlide}
            sx={{
              position: "absolute",
              right: styles.arrow.position,
              top: "50%",
              transform: "translateY(-50%)",
              zIndex: 20,
              width: styles.arrow.size,
              height: styles.arrow.size,
              bgcolor: sliderStyles.arrowBackground.default,
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              border: "none",
              cursor: "pointer",
              backdropFilter: "blur(4px)",
              transition: "all 0.2s",
              "&:hover": { bgcolor: sliderStyles.arrowBackground.hover },
            }}
            aria-label="Next slide"
          >
            <Box
              component="svg"
              sx={{
                width: styles.arrow.iconSize,
                height: styles.arrow.iconSize,
                color: "#ffffff",
              }}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </Box>
          </Box>
        </>
      )}

      {/* Dots - using config */}
      {showDots && slides.length > 1 && (
        <Box
          sx={{
            position: "absolute",
            bottom: styles.dot.bottom,
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 20,
            display: "flex",
            gap: `${styles.dot.gap}px`,
          }}
        >
          {slides.map((_, index) => (
            <Box
              key={index}
              component="button"
              onClick={() => goToSlide(index)}
              sx={{
                width: index === currentSlide ? styles.dot.activeWidth : styles.dot.inactiveWidth,
                height: styles.dot.height,
                borderRadius: `${styles.dot.borderRadius}px`,
                border: "none",
                cursor: "pointer",
                transition: "all 0.2s",
                bgcolor:
                  index === currentSlide ? sliderStyles.dotBackground.active : sliderStyles.dotBackground.inactive,
                "&:hover": {
                  bgcolor: index === currentSlide ? sliderStyles.dotBackground.active : "rgba(255, 255, 255, 0.75)",
                },
              }}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </Box>
      )}
    </Box>
  );
};

// Settings Panel
export const HeroSliderSettings = () => {
  const {
    actions: { setProp },
    slides,
    minHeight,
    autoPlay,
    autoPlayInterval,
    showDots,
    showArrows,
  } = useNode((node) => ({
    slides: node.data.props.slides,
    minHeight: node.data.props.minHeight,
    autoPlay: node.data.props.autoPlay,
    autoPlayInterval: node.data.props.autoPlayInterval,
    showDots: node.data.props.showDots,
    showArrows: node.data.props.showArrows,
  }));

  const [editingSlide, setEditingSlide] = useState<number | null>(null);

  const addSlide = () => {
    const newSlide: HeroSlide = {
      id: Date.now().toString(),
      title: "New Slide",
      subtitle: "Add your subtitle",
      buttonText: "Click Here",
      buttonLink: "#",
      backgroundColor: "#1e3a5f",
    };
    setProp((props: HeroSliderProps) => {
      props.slides = [...(props.slides || []), newSlide];
    });
  };

  const removeSlide = (index: number) => {
    setProp((props: HeroSliderProps) => {
      props.slides = props.slides.filter((_, i) => i !== index);
    });
    setEditingSlide(null);
  };

  const updateSlide = (index: number, updates: Partial<HeroSlide>) => {
    setProp((props: HeroSliderProps) => {
      props.slides = props.slides.map((slide, i) => (i === index ? { ...slide, ...updates } : slide));
    });
  };

  return (
    <SettingsSection>
      {/* Slides List */}
      <div>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "8px" }}>
          <label style={labelStyle}>Slides ({slides?.length || 0})</label>
          <button onClick={addSlide} style={{ ...addButtonStyle, width: "auto", marginTop: 0 }}>
            + Add
          </button>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: "8px", maxHeight: "192px", overflowY: "auto" }}>
          {slides?.map((slide: HeroSlide, index: number) => (
            <div
              key={slide.id}
              onClick={() => setEditingSlide(editingSlide === index ? null : index)}
              style={{
                padding: "8px",
                border: `1px solid ${editingSlide === index ? "#3b82f6" : "#d1d5db"}`,
                borderRadius: "4px",
                cursor: "pointer",
                backgroundColor: editingSlide === index ? "#eff6ff" : "#ffffff",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <span
                  style={{
                    fontSize: "0.875rem",
                    fontWeight: 500,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {slide.title}
                </span>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    removeSlide(index);
                  }}
                  style={removeButtonStyle}
                >
                  ✕
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Edit Selected Slide */}
      {editingSlide !== null && slides?.[editingSlide] && (
        <div style={itemCardStyle}>
          <h4 style={{ fontWeight: 500, fontSize: "0.875rem", margin: "0 0 12px 0" }}>Edit Slide {editingSlide + 1}</h4>

          <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
            <div>
              <label style={labelStyle}>Title</label>
              <input
                type="text"
                value={slides[editingSlide].title}
                onChange={(e) => updateSlide(editingSlide, { title: e.target.value })}
                style={inputStyle}
              />
            </div>

            <div>
              <label style={labelStyle}>Subtitle</label>
              <input
                type="text"
                value={slides[editingSlide].subtitle || ""}
                onChange={(e) => updateSlide(editingSlide, { subtitle: e.target.value })}
                style={inputStyle}
              />
            </div>

            <div>
              <div
                style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "4px" }}
              >
                <label style={labelStyle}>Button</label>
                {slides[editingSlide].buttonText && (
                  <button
                    onClick={() => updateSlide(editingSlide, { buttonText: "", buttonLink: "" })}
                    style={removeButtonStyle}
                  >
                    Remove
                  </button>
                )}
              </div>
              <div style={gridTwoColumnsStyle}>
                <input
                  type="text"
                  value={slides[editingSlide].buttonText || ""}
                  onChange={(e) => updateSlide(editingSlide, { buttonText: e.target.value })}
                  style={inputStyle}
                  placeholder="Button text"
                />
                <input
                  type="text"
                  value={slides[editingSlide].buttonLink || ""}
                  onChange={(e) => updateSlide(editingSlide, { buttonLink: e.target.value })}
                  style={inputStyle}
                  placeholder="Button link"
                />
              </div>
            </div>

            <ImageUpload
              label="Background Image"
              value={slides[editingSlide].backgroundImage || ""}
              onChange={(url) => updateSlide(editingSlide, { backgroundImage: url })}
            />

            {slides[editingSlide].backgroundImage && (
              <ImageCropPicker
                backgroundImage={slides[editingSlide].backgroundImage!}
                backgroundPosition={slides[editingSlide].backgroundPosition}
                backgroundZoom={slides[editingSlide].backgroundZoom}
                onPositionChange={(v) => updateSlide(editingSlide, { backgroundPosition: v })}
                onZoomChange={(v) => updateSlide(editingSlide, { backgroundZoom: v })}
              />
            )}

            <ColorField
              label="Background Color"
              value={slides[editingSlide].backgroundColor || "#1e3a5f"}
              onChange={(v) => updateSlide(editingSlide, { backgroundColor: v })}
            />
          </div>
        </div>
      )}

      {/* Global Settings */}
      <div style={{ borderTop: "1px solid #e5e7eb", paddingTop: "16px" }}>
        <h4 style={{ fontWeight: 500, fontSize: "0.875rem", margin: "0 0 12px 0" }}>Slider Settings</h4>

        <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
          <SliderField
            label="Min Height"
            value={minHeight}
            onChange={(v) => setProp((props: HeroSliderProps) => (props.minHeight = v))}
            min={300}
            max={800}
            unit="px"
          />

          <div style={{ display: "flex", flexWrap: "wrap", gap: "12px" }}>
            <CheckboxField
              label="Auto Play"
              checked={autoPlay}
              onChange={(v) => setProp((props: HeroSliderProps) => (props.autoPlay = v))}
            />
            <CheckboxField
              label="Dots"
              checked={showDots}
              onChange={(v) => setProp((props: HeroSliderProps) => (props.showDots = v))}
            />
            <CheckboxField
              label="Arrows"
              checked={showArrows}
              onChange={(v) => setProp((props: HeroSliderProps) => (props.showArrows = v))}
            />
          </div>

          {autoPlay && (
            <SliderField
              label="Interval"
              value={autoPlayInterval / 1000}
              onChange={(v) => setProp((props: HeroSliderProps) => (props.autoPlayInterval = v * 1000))}
              min={2}
              max={10}
              step={0.5}
              unit="s"
            />
          )}
        </div>
      </div>
    </SettingsSection>
  );
};

HeroSlider.craft = {
  displayName: "Hero Slider",
  props: {
    slides: defaultSlides,
    minHeight: 500,
    autoPlay: true,
    autoPlayInterval: 5000,
    showDots: true,
    showArrows: true,
    titleColor: "#ffffff",
    subtitleColor: "#e5e7eb",
  },
  related: {
    settings: HeroSliderSettings,
  },
};
